// Motor de la tabla de Inventario: TanStack Table con filtro, orden y páginas sobre lo cargado.
// La tabla se pinta con el `DataTable` compartido; esto solo decide qué filas y en qué orden.
import { useMemo } from 'react';
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
    type OnChangeFn,
    type SortingState,
    type Table,
} from '@tanstack/react-table';
import {
    compareRows,
    matchesFilters,
    sortValueOf,
    type InventoryFilters,
    type InventoryRow,
    type SortColumn,
    type SortState,
} from './inventoryList';
import { COLUMNS } from './inventoryFormat';

/** Filas por página de lo ya cargado. */
export const TABLE_PAGE_SIZE = 25;

const SORT_COLUMNS: SortColumn[] = COLUMNS.map((c) => c.column);

/** El filtro corre una vez por fila: solo esta columna se declara alcanzable por la búsqueda global. */
const GLOBAL_FILTER_COLUMN: SortColumn = 'name';

export interface InventoryTableModel {
    table: Table<InventoryRow>;
    /** Las filas de la página visible, con referencia estable entre renders. */
    pageRows: InventoryRow[];
    /** Cuántas filas pasan los filtros. */
    filteredCount: number;
}

export function useInventoryTable(input: {
    /** Todas las filas; tiene que venir memoizada por quien llama. */
    rows: InventoryRow[];
    filters: InventoryFilters;
    sort: SortState;
    onSortChange: (next: SortState) => void;
}): InventoryTableModel {
    const { rows, filters, sort, onSortChange } = input;

    const columns = useMemo<ColumnDef<InventoryRow>[]>(
        () =>
            SORT_COLUMNS.map((column) => ({
                id: column,
                // Sin dato va `undefined`: `sortUndefined: 'last'` lo deja al fondo en ambos sentidos.
                accessorFn: (row) => sortValueOf(row, column) ?? undefined,
                sortUndefined: 'last' as const,
                enableGlobalFilter: column === GLOBAL_FILTER_COLUMN,
                sortingFn: (left, right) => compareRows(left.original, right.original, column),
            })),
        [],
    );

    const sorting = useMemo<SortingState>(
        () => [{ id: sort.column, desc: sort.direction === 'desc' }],
        [sort],
    );

    const onSortingChange: OnChangeFn<SortingState> = (updater) => {
        const next = typeof updater === 'function' ? updater(sorting) : updater;
        if (next.length === 0) return;
        onSortChange({
            column: next[0].id as SortColumn,
            direction: next[0].desc ? 'desc' : 'asc',
        });
    };

    const table = useReactTable({
        data: rows,
        columns,
        state: { sorting, globalFilter: filters },
        onSortingChange,
        // El estado del filtro es el objeto entero; la regla es la misma de los conteos.
        globalFilterFn: (row, _columnId, value: InventoryFilters) =>
            matchesFilters(row.original, value),
        getColumnCanGlobalFilter: (column) => column.id === GLOBAL_FILTER_COLUMN,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: TABLE_PAGE_SIZE } },
    });

    const modelRows = table.getRowModel().rows;
    const pageRows = useMemo(() => modelRows.map((row) => row.original), [modelRows]);

    return { table, pageRows, filteredCount: table.getFilteredRowModel().rows.length };
}
